/**
 * Anchor-watch depth and rode readout (feedback fb-mrwmqx8n-b: "what is the
 * depth option for here?").
 *
 * When we drop the hook we want three things spelled out before we trust the
 * swing: the depth where the anchor is now, and how that depth changes at the
 * next Low Water (the shallow extreme, where we might touch) and the next High
 * Water (the deep extreme, which is what sizes the rode). All of it is built from
 * the charted depth (relative to chart datum, from the vector ENC) plus the
 * height of tide, so it's honest about what it knows: no charted depth, no
 * reading.
 *
 * Kept pure so the sums are unit-testable without a chart or a tide feed; the
 * sheet fetches the charted depth and the LW/HW heights and hands them in.
 */

/** One depth reading: the tide height used, the water depth, and the clearance. */
export interface AnchorTideDepth {
  /** Height of tide above chart datum used for this reading, metres, or null. */
  tideM: number | null;
  /** Water depth = charted depth to datum + tide, metres, or null when unknown. */
  waterDepthM: number | null;
  /** Clearance under the keel = water depth − draught, metres, or null. */
  underKeelM: number | null;
}

export interface AnchorReadout {
  /** Charted depth to chart datum at the anchor, metres, or null (raster/none). */
  chartedM: number | null;
  /** Depth now, at the next Low Water, and at the next High Water. */
  now: AnchorTideDepth;
  lw: AnchorTideDepth;
  hw: AnchorTideDepth;
  /**
   * Suggested rode to let out, metres, or null when there's no charted depth to
   * size it from. Taken at High Water (the deepest the boat will sit, so the most
   * rode it needs): (HW water depth + bow-roller height) × scope ratio.
   */
  rodeM: number | null;
  /** The scope ratio the rode was sized with (rode : vertical drop). */
  scopeRatio: number;
}

/** Build one depth reading from the charted datum depth, a tide height, and draught. */
export function tideDepth(
  chartedM: number | null,
  tideM: number | null,
  draughtM: number,
): AnchorTideDepth {
  if (chartedM == null) return { tideM, waterDepthM: null, underKeelM: null };
  const waterDepthM = chartedM + (tideM ?? 0);
  return { tideM, waterDepthM, underKeelM: waterDepthM - draughtM };
}

export interface AnchorReadoutInput {
  /** Charted depth to chart datum at the anchor, metres, or null. */
  chartedM: number | null;
  /** Height of tide now, metres above datum, or null. */
  tideNowM: number | null;
  /** Height at the next Low Water, metres above datum, or null when unknown. */
  tideLwM: number | null;
  /** Height at the next High Water, metres above datum, or null when unknown. */
  tideHwM: number | null;
  /** Boat draught, metres. */
  draughtM: number;
  /** Height of the bow roller above the water, metres. */
  bowHeightM: number;
  /** Desired scope ratio (rode : vertical drop), e.g. 4 on chain, 6 on rope. */
  scopeRatio: number;
}

/**
 * The full anchor readout: depth now / at LW / at HW, and a suggested rode sized
 * at High Water. Rode = (HW water depth + bow-roller height) × scope, because the
 * scope has to be enough when the boat sits highest; the tide is already folded
 * into the HW water depth, so there's no separate tide-rise term to add.
 */
export function anchorReadout(i: AnchorReadoutInput): AnchorReadout {
  const now = tideDepth(i.chartedM, i.tideNowM, i.draughtM);
  const lw = tideDepth(i.chartedM, i.tideLwM, i.draughtM);
  const hw = tideDepth(i.chartedM, i.tideHwM, i.draughtM);
  // Size the rode from the deepest the boat will sit. Prefer High Water, but only
  // when we actually have an HW height; with no HW prediction, fall back to the
  // depth now (which already includes the current tide) rather than bare datum.
  const sizingWaterDepth = i.tideHwM != null ? hw.waterDepthM : now.waterDepthM;
  const rodeM =
    sizingWaterDepth == null ? null : (sizingWaterDepth + i.bowHeightM) * i.scopeRatio;
  return { chartedM: i.chartedM, now, lw, hw, rodeM, scopeRatio: i.scopeRatio };
}
